import { WidgetComponentType } from "models/screenshot";

import { ScreenshotSetting } from "components/screenshot/components";
import {
  SettingsControl,
  SettingsGroups,
  useScreenshotContext
} from "components/screenshot/hooks";

function useXDAScreenshotSettingsControls(): SettingsControl[] {
  // hooks
  const { widget, settings } = useScreenshotContext();

  const XDASettingsGroups = {
    threeDGeoLegend: "3D Geo Model Legend",
    // EVA-29: the spacing measurement legend (Lateral/Stack/Stagger) is a
    // separate legend from the 3D geo model one, so it gets its own controls.
    spacingLegend: "Spacing Legend"
  };

  const controls: SettingsControl[] = [];

  if (widget?.widgetComponentType !== WidgetComponentType.XdaViewer) {
    return controls;
  }

  controls.push(
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="topsLabelsFontSize"
          name="topsLabelsFontSize"
          label="Tops Labels Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="dataLabelsFontSize"
          name="dataLabelsFontSize"
          label="Data Labels Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="gridLabelsFontSize"
          name="gridLabelsFontSize"
          label="Grid Labels Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="legendTitleSize"
          name="legendTitleSize"
          label="Legend Title Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="legendFontSize"
          name="legendFontSize"
          label="Legend Label Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    // "Show Legend" sits in the shared Preset group. It moved out to the 3D Geo
    // Model Legend group while the spacing legend existed, because among the
    // shared label sizes it read as a master switch over BOTH legends. With
    // the spacing legend removed (EVA-163 comment 163139) there is only one
    // legend left for it to mean, so the ambiguity is gone and it goes back.
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="legendVisible"
          name="legendVisible"
          label="Show Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    }
    /* PARKED (EVA-163 comment 163139) — the spacing legend is removed from the
       widget and the screenshot, so it has no settings group.
    {
      group: XDASettingsGroups.spacingLegend,
      control: (
        <ScreenshotSetting.Item
          key="spacingLegendVisible"
          name="spacingLegendVisible"
          label="Show Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    }
    */
  );
  // Border and opacity only mean something while their legend is showing, and
  // each is gated on ITS OWN switch — turning the geo model legend off used to
  // take the spacing legend's controls down with it.
  if (settings?.legendVisible) {
    controls.push(
      {
        group: XDASettingsGroups.threeDGeoLegend,
        control: (
          <ScreenshotSetting.Item
            key="legendBorderVisible"
            name="legendBorderVisible"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: XDASettingsGroups.threeDGeoLegend,
        control: (
          <ScreenshotSetting.Item
            key="legendOpacity"
            name="legendOpacity"
            label="Legend Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }
  /* PARKED (EVA-163 comment 163139) — border and opacity for a legend
     that is no longer rendered.
  // Mirrors XdaViewer's `activeSpacingLegendVisible` exactly — absent means ON
  // (the legend defaults to showing), anything else goes through Boolean, so a
  // serialized `null` in an old preset hides the dependent controls rather
  // than offering Border and Opacity for a legend that is not on screen.
  const spacingLegendShowing =
    settings?.spacingLegendVisible === undefined ||
    Boolean(settings.spacingLegendVisible);
  if (spacingLegendShowing) {
    controls.push(
      {
        group: XDASettingsGroups.spacingLegend,
        control: (
          <ScreenshotSetting.Item
            key="spacingLegendBorderVisible"
            name="spacingLegendBorderVisible"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: XDASettingsGroups.spacingLegend,
        control: (
          <ScreenshotSetting.Item
            key="spacingLegendOpacity"
            name="spacingLegendOpacity"
            label="Legend Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }
  */

  return controls;
}

export default useXDAScreenshotSettingsControls;
